import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import Tabs from '../components/Tabs';
import { usePersistentValue } from '../hooks/usePersistentState';
import { useHubExcelExport } from '../components/ExcelExportButton';
import { useRegisterPageExports } from '../exports/pageExports';

import { resolveTabs } from '../i18n/resolveTabs';
import { useP } from '../i18n/useP';
import InfoTooltip from '../components/InfoTooltip';
import CcrStory from './hr/CcrStory';
import PoolAdvice from './hr/PoolAdvice';
import CostPoolsPage from './tdabc/CostPoolsPage';
import TdabcPeoplePage from './tdabc/PeoplePage';
import FoldCard from '../components/FoldCard';
import CostAssignmentPage from './tdabc/CostAssignmentPage';
import TimeEffectivePage from './hr/TimeEffectivePage';
import TdabcPrintDocument from './tdabc/TdabcPrintDocument';


// Toggle « + Définir les groupes / Masquer » — bouton lien accent (cp-add-row-btn)
// posé SOUS le titre « Groupes ressources » (plus visible), aligné à gauche.

/* Coût horaire (CCR) — hub à onglets construisant le coût horaire de capacité :
   temps standard (occupation annuelle par personne = synthèse effective, source
   UNIQUE du carve corp/supervision/congés spéciaux qui pilote le TR) → affectation
   personnel + affectation frais (cascade vers les groupes ressources) → CCR (coût
   complet ÷ capacité pratique). Affectations / CCR = CostAssignmentPage (pageView
   personnel / frais / total). Le salaire horaire (LCR) vit dans RH ; le coût de
   production (BCR) en ABC.
   Onglet par défaut = « Capacité horaire (CCR) » : on arrive sur la synthèse du
   calcul, alimentée par les onglets de saisie (temps standard + affectations).
   Barre d'onglets (aside) : bascule CHF ⇄ kCHF (montants uniquement, sur les vues
   d'affectation/CCR) + export PDF + export Excel — comme les hubs Finance.
   Gabarit unique : <PageShell framed> + <Tabs> + sous-pages en mode embedded. */

/* La section s'ouvre sur LE RÉCIT (2026-08-22) : la méthode chapitre par
   chapitre, puis la table par personne qu'elle conclut. Les trois onglets de
   réglage l'accompagnent — ils ont fait un aller-retour par « Paramètres » le
   temps d'une journée, mais ce sont les entrées du CCR et de lui seul : les
   voir ici évite de quitter la section pour ajuster ce qu'on vient de lire.
   `sep` marque le passage de la lecture au réglage.
   Le « Temps standard » reste dans la section Temps : c'est une saisie
   d'heures, le CCR la LIT sans la porter. */
const TAB_DEFS = [
  { id: 'story',     labelKey: 'people:ccr.tabs.story' },
  { id: 'ccr',       labelKey: 'people:ccr.tabs.ccr' },
  /* `hidden` : l'onglet reste MONTABLE (la section « Groupe ressources » le
     sert via `only`) mais quitte la barre du hub — 2026-08-30. « Groupe » y
     doublait sa propre section, et « Temps » doublait « Saisie des temps ».
     La saisie se centralise là-bas, le hub ne fait plus que lire. */
  { id: 'settings',  labelKey: 'people:ccr.tabs.settings', sep: true, hidden: true },
  { id: 'standard',  labelKey: 'people:ccr.tabs.time', hidden: true },
  /* `hidden` aussi depuis le 2026-09-01 : leurs vues vivent DANS l'onglet
     CCR, derrière les tuiles « Personnel indirect » et « Frais généraux ».
     Montables (export Excel, liens), plus dans la barre. */
  { id: 'personnel', labelKey: 'people:ccr.tabs.personnel', hidden: true },
  { id: 'frais',     labelKey: 'people:ccr.tabs.frais', hidden: true },
];

/* TROIS cards dépliables — l'onglet empilait auparavant ses blocs sous des
   intertitres en petites capitales, sans hiérarchie. Chaque card répond à une
   question différente : comment décider, que décider, à qui l'appliquer. La
   première est un mode d'emploi et se relit ; les deux autres se saisissent. */
const IconGroups = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </svg>
);
const IconHelp = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 2.5-3 4" />
    <path d="M12 17.5h.01" />
  </svg>
);
const IconAssign = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="m16 11 2 2 4-4" />
  </svg>
);

// Onglets dont l'affichage comporte des montants (bascule CHF/kCHF pertinente).
const MONEY_TABS = new Set(['ccr', 'story', 'personnel', 'frais']);

export default function RatesHub({ activeTenant, addToast, year, onYearChange, setPage, initialTab, only = null, title = null }) {
  const { t } = useTranslation();
  const p = useP();
  /* `only` : le hub sert alors une SECTION, réduite à cet onglet ou à cette
     liste d'onglets (2026-08-27, « Groupe » est devenu une section de Saisie
     et réglages). Un onglet unique n'a pas de barre : rien à choisir. */
  const keep = only == null ? null : (Array.isArray(only) ? only : [only]);
  const defs = keep ? TAB_DEFS.filter(d => keep.includes(d.id)) : TAB_DEFS.filter(d => !d.hidden);
  /* `initialTab` : onglet visé par un lien venu d'une autre section. */
  const [tab, setTab] = useState((keep && keep[0]) || initialTab || 'story');
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
  useEffect(() => { if (keep && !keep.includes(tab)) setTab(keep[0]); }, [keep, tab]);
  const TABS = resolveTabs(defs, t);
  // Version des groupes ressources : bumpée par « Groupes ressources » (CRUD) →
  // force « Affectation des personnes » à recharger sa liste (source cohérente).
  const [unit, setUnit] = usePersistentValue(`ccr_hub_unit_${activeTenant?.id}`, 'kchf');
  /* L'unité monte à la BANDE DE CONTEXTE, comme dans Finance (2026-09-18) :
     elle tenait la droite de la ligne des onglets, où elle changeait de place
     d'un onglet à l'autre. Le gabarit la place, la page la déclare. */
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);
  const [showPrint, setShowPrint] = useState(false);
  // Bumpée par le CRUD des groupes → force l'affectation à recharger sa liste.
  const [poolsVersion, setPoolsVersion] = useState(0);

  // Rendu d'un onglet — à l'écran (unité du hub) et pour l'export Excel (CHF forcé).
  const renderTab = (id, forExport = false) => {
    const u = forExport ? 'chf' : unit;
    switch (id) {
      case 'story':
        return <CcrStory part="ccr" activeTenant={activeTenant} year={year} unit={u} setPage={setPage}
                         onGoDetail={() => setTab('ccr')} onGoTab={setTab} />;
      case 'ccr':
        /* À l'écran, la vue Total s'ouvre sur le GROUPE : des barres empilées,
           dessinées en <div>, sans aucun tableau — la feuille « Capacité horaire
           (CCR) » sortait donc vide. Pour l'export on demande la sous-vue
           `detail`, qui est la table du CCR par personne. */
        return <CostAssignmentPage embedded pageView="total" unit={u}
                                   totalSubview={forExport ? 'detail' : null}
                                   activeTenant={activeTenant} addToast={addToast} year={year} onYearChange={onYearChange} />;
      case 'personnel':
        return <CostAssignmentPage embedded pageView="personnel" unit={u} activeTenant={activeTenant} addToast={addToast} year={year} onYearChange={onYearChange} />;
      case 'frais':
        return <CostAssignmentPage embedded pageView="frais" unit={u} setPage={setPage} activeTenant={activeTenant} addToast={addToast} year={year} onYearChange={onYearChange} />;
      case 'standard':
        return <TimeEffectivePage embedded activeTenant={activeTenant} addToast={addToast}
                                  year={year} onYearChange={onYearChange} />;
      case 'settings':
        return (
          <div className="fcard-stack">
            {/* Le découpage décide de la qualité du coût horaire, et rien
                n'aidait à le faire : on arrivait sur une liste vide. Le mode
                d'emploi mérite donc sa propre card — repliée, on ne le relit
                pas à chaque visite. */}
            <FoldCard icon={<IconHelp />} defaultOpen={false}
                      title={p('Comment définir ses groupes')}
                      subtitle={p('Trois tests avant de découper')}>
              <PoolAdvice />
            </FoldCard>
            <FoldCard icon={<IconGroups />}
                      title={p('Définissez vos groupes')}
                      subtitle={p('Ce que le coût horaire saura distinguer')}>
              <CostPoolsPage embedded activeTenant={activeTenant} addToast={addToast}
                             onPoolsChanged={() => setPoolsVersion((v) => v + 1)} />
            </FoldCard>
            <FoldCard icon={<IconAssign />}
                      title={p('Affecter les collaborateurs')}
                      subtitle={p('Sans groupe, le coût d’une personne reste hors périmètre')}>
              <TdabcPeoplePage embedded activeTenant={activeTenant} addToast={addToast} year={year} poolsVersion={poolsVersion} />
            </FoldCard>
          </div>
        );
      default:
        return null;
    }
  };

  // Exports de l'écran (PDF · Excel) — déclarés au registre, rendus par l'icône
  // télécharger de la barre supérieure. La rangée d'onglets ne garde que ce qui
  // AGIT sur l'écran : la bascule d'unité.
  /* Onglets exportables : le récit est un texte à défilement, il n'a aucun
     tableau à donner. Il est ÉCARTÉ de la liste plutôt que de rendre `null` —
     un conteneur vide serait indiscernable d'un onglet encore en chargement, et
     l'export se croirait tronqué. */
  /* Les vues cachées de la barre restent des FEUILLES : l'export couvre le
     CCR et ses deux affectations, comme quand elles étaient des onglets. */
  const EXPORT_TABS = resolveTabs(
    TAB_DEFS.filter((tb) => ['ccr', 'personnel', 'frais'].includes(tb.id)), t);
  const excel = useHubExcelExport({
    tabs: EXPORT_TABS.map(tb => ({ label: tb.label, render: () => renderTab(tb.id, true) })),
    fileBase: t('phrases:p_b9fa3a5448'),
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  useRegisterPageExports(useMemo(() => ([
    { id: 'pdf', label: 'PDF', run: () => setShowPrint(true) },
    { id: 'excel', label: 'Excel', run: excel.run },
  ]), [excel.run]));

  const viewControls = null;   // l'unité vit dans la bande de contexte

  return (
    <PageShell
      framed
      kind="report"
      unit={MONEY_TABS.has(tab) ? pageUnit : null}
      title={title || <>{t('people:ccr.title')}<InfoTooltip term="ccr" /></>}
    >
      {/* Plus de flèche `flow` : elle disait un ordre de CONSTRUCTION entre
          quatre onglets de saisie. Il n'en reste que deux, et ce sont deux
          lectures — le résumé, puis le détail. */}
      {TABS.length > 1 && <Tabs tabs={TABS} active={tab} onChange={setTab} aside={viewControls} />}

      <div className="page-shell__panel">
        {renderTab(tab)}
      </div>
      {excel.stage}

      {showPrint && (
        <TdabcPrintDocument activeTenant={activeTenant} year={year} unit={unit} onClose={() => setShowPrint(false)} />
      )}
    </PageShell>
  );
}

